import AnalyticsCard from "features/analytics/components/AnalyticsCard";
import AnalyticsCardPanelHeader from "features/analytics/components/AnalyticsCardPanelHeader";
import { useChartData } from "features/analytics";
import { groupBy } from "Helpers/arrays";
import { formatNumber } from "Helpers/numbers";
import { truncate } from "Helpers/truncate";
import { useSelectedProject } from "Hooks";
import { useFromToContext } from "Providers/FromToProvider";
import { useMemo } from "react";
import { ApiDimension, ApiMetric, ApiYAxisChartType } from "@incendium/api";
import { StyledComposedChart } from "features/analytics";
import { TouchApp } from "@mui/icons-material";
import { Box } from "@mui/material";

interface IPrimaryTopMicroConversionsProps {
  byDimension: string;
  dimension: ApiDimension;
  dimensionString: string;
}

function PrimaryTopMicroConversions({
  byDimension,
  dimension,
  dimensionString,
}: IPrimaryTopMicroConversionsProps) {
  const { selectedProject } = useSelectedProject();
  const { chartOutput } = useFromToContext();

  // todo, swap ou for rate, when sorted, https://incendium.atlassian.net/browse/IP-2702
  const metric = ApiMetric.METRIC_PAGEVIEWS_COUNT;

  const chart = useMemo(
    () => ({
      name: "Top Interactions",
      dimension: [dimension, ApiDimension.DIMENSION_MICRO_CONVERSION],
      yAxisKeys: [
        {
          fields: [metric],
        },
      ],
      attributes: [],
    }),
    [dimension, metric]
  );

  const { chartData } = useChartData(selectedProject, chart, chartOutput);

  const current = useMemo(() => {
    if (!chartData?.data) {
      return [];
    }
    const gg = groupBy(chartData?.data || [], "name");
    return (gg[byDimension] || []).filter(
      (d) => d[ApiDimension.DIMENSION_MICRO_CONVERSION]
    );
  }, [chartData, byDimension]);

  const average = useMemo(() => {
    if (!chartData?.data) {
      return [];
    }
    const gg = groupBy(
      chartData?.data || [],
      ApiDimension.DIMENSION_MICRO_CONVERSION
    );

    return Object.keys(gg)
      .map((k) => {
        const arr = gg[k];
        const sum = (arr || []).reduce((acc: any, v, i, a) => {
          const { name, ...rest } = v;

          acc[name] = name;
          acc[ApiDimension.DIMENSION_MICRO_CONVERSION] =
            rest[ApiDimension.DIMENSION_MICRO_CONVERSION];

          if (!acc[metric]) {
            acc[metric] = 0;
          }
          acc[metric] = acc[metric] + Number(rest[metric]);
          return acc;
        }, {});

        sum[metric] = sum[metric] / arr.length;
        return sum;
      })
      .filter((d) => d[ApiDimension.DIMENSION_MICRO_CONVERSION]);
  }, [chartData, metric]);

  const parsedChartData = useMemo(() => {
    return current
      .sort((a, b) => Number(b[metric] || 0) - Number(a[metric] || 0))
      .slice(0, 6)
      .map((c) => ({
        name: truncate(
          `${c[ApiDimension.DIMENSION_MICRO_CONVERSION]}` || "",
          15
        ),
        current: Number(c[metric] || 0),
        average: Number(
          average.find(
            (a) =>
              a[ApiDimension.DIMENSION_MICRO_CONVERSION] ===
              c[ApiDimension.DIMENSION_MICRO_CONVERSION],
          )?.[metric] || 0,
        ),
      }));
  }, [current, average, metric]);

  const topInteraction = parsedChartData[0];

  return (
    <AnalyticsCard
      noToolbar
      chart={{
        ...chart,
        displayOptions: {
          noTitle: true,
        },
      }}
      sx={{ height: "100%", width: "100%" }}
      boxProps={{ px: 2, pt: 2.5, pb: 2, height: "100%" }}
      customHeader={
        <AnalyticsCardPanelHeader
          icon={<TouchApp fontSize="small" />}
          title="Top Interactions"
          statLabel={
            topInteraction
              ? `${formatNumber(topInteraction.current)} ${topInteraction.name}`
              : undefined
          }
        />
      }
      body={
        <Box sx={{ height: "100%", minHeight: 280, width: "100%" }}>
          <StyledComposedChart
            data={parsedChartData}
            yAxisKeys={[
              {
                key: "l",
                fields: ["average", "current"],
                chart: {
                  average: ApiYAxisChartType.BAR,
                  current: ApiYAxisChartType.BAR,
                },
              },
            ]}
            noReOrder
            showTooltip
          />
        </Box>
      }
    />
  );
}

export default PrimaryTopMicroConversions;
